<template>
  <div class="wdw-create-attribute">
    <wdw-dynbox :title="attribute ? 'Zusatzfeld bearbeiten' : 'Zusatzfeld anlegen'" :minimize="false">
      <!-- Feedback Messages -->
      <Message v-for="msg of messages" :severity="msg.severity" :key="msg.content" :closable="true">
        {{ msg.content }}
      </Message>
      <!-- Loading Indicator -->
      <template v-if="!userIsAdmin">
        <Message severity="error" key="Keine Berechtigung Zusatzfelder anzulegen" :closable="false">
          Keine Berechtigung Zusatzfelder anzulegen
        </Message>
      </template>

      <!-- Actual content / attribute create form -->
      <template v-else>
        <template v-if="loader.initialising">
          <loading loadingMessage="Loading..." />
        </template>
        <template v-else>
          <ValidationForm v-if="!loader.submitted" @submit="onSubmitForm">
            <div id="wdw-create-attribute-form" class="wdw-form">
              <form-control-input
                v-for="field in input_field_definitions"
                v-bind:key="field.name"
                :name="field.name"
                :code="field.name"
                :label="field.label"
                :maxlength="field.max_length || 0"
                prefixId="wdw-create-attribute-form"
                :required="field.required"
                :defaultvalue="field.default"
              />
              <form-control-select
                label="Feldtyp"
                name="type"
                :options="type_options"
                :required="true"
                :defaultvalue="attribute ? [attribute.type] : ['text']"
                @selected="onTypeSelect"
                :disabled="attribute ? true : false"
              />

              <template v-if="typeValuelist">
                <template v-if="loader.fetchingValuelist">
                  <loading loadingMessage="Loading..." />
                </template>
                <template v-else>
                  <form-control-select
                    v-if="!createNewValuelist && availableValuelistOptions.length > 0"
                    label="Existierende Werteliste verwenden"
                    name="valuelistid"
                    :options="availableValuelistOptions"
                    :required="showValuelistSelect && !createNewValuelist ? true : false"
                  />
                  <div class="alternative-create-valuelist-btn" v-if="!createNewValuelist">
                    oder
                    <button type="button" class="btn btn-primary" @click="showCreateValuelist">
                      Neue Werteliste anlegen
                    </button>
                  </div>
                  <ValuelistFormVue v-if="createNewValuelist" @changed-entries="updateNewValuelist" />
                </template>
              </template>
              <form-control-select
                label="Eingabe durch"
                name="visible_to"
                :options="visible_to_options"
                :required="true"
                :defaultvalue="['both']"
              />
              <form-control-checkbox
                v-for="field in checkbox_field_definitions"
                v-bind:key="field.name"
                :name="field.name"
                :code="field.name"
                :label="field.label"
                prefixId="wdw-create-attribute-form"
                :defaultvalue="field.default"
              />
              <button type="submit" :disabled="loader.submitting" class="btn btn-primary btn-form-submit" name="Save">
                Speichern
              </button>
              &nbsp;
              <button type="button" class="btn btn-default btn-form-cancel" @click="emitCancelThoughtForm()">
                Abbrechen
              </button>
            </div>
          </ValidationForm>
        </template>

        <loading v-if="showLoading" loadingMessage="Loading..." />
      </template>
    </wdw-dynbox>
  </div>
</template>

<script>
/*
  short description:
  - component is integrated in wdw-server/admin as a standalone component
  - component should be able to work as a standalone component even if we decide to integrate it into other components in the future
*/
import { onBeforeMount, ref, computed } from 'vue';

// stores
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';

// components
import { Form as ValidationForm } from 'vee-validate';
import Message from 'primevue/message';
import Loading from '@/components/general/Loading.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox';
import FormControlSelect from '@/components/general/FormControl/Select';
import ValuelistFormVue from '@/components/attribute/ValuelistForm.vue';

// composables
import useAuthentication from '@/composables/user/useAuthentication';

// repository
import { RepositoryFactory } from '@/api/repositoryFactory';
const AttributeRepository = RepositoryFactory.get('attribute');

export default {
  name: 'AttributeEditor',

  props: {
    domainid: {
      required: true,
      type: Number,
    },
    parent_type: {
      required: true,
      type: String,
    },
    parent_id: {
      required: true,
      type: Number,
    },
    redirect_after_save: {
      required: true,
      type: String,
    },
    attribute_id: {
      required: false,
      type: Number,
    },
  },

  emits: ['submitted'],

  setup(props, context) {
    const { initStorageAuthToken } = useAuthentication();

    const selectedTopic = ref();
    const selectedThought = ref();
    const messages = ref([]);
    const availableValuelistOptions = ref([]);
    const createNewValuelist = ref(false);
    const typeValuelist = ref(false);
    const newValuelistEntries = ref([]);
    const attribute = ref(null);

    const loader = ref({
      initialising: true,
      submitting: false,
      fetchingValuelist: true,
      submitted: false,
    });

    const input_field_definitions = ref([]);
    const checkbox_field_definitions = ref([]);

    const visible_to_options = [
      { id: 'both', text: 'Bürger und Verwaltung' },
      { id: 'user', text: 'Nur Bürgerinnen und Bürger' },
      { id: 'moderator', text: 'Nur Verwaltung' },
    ];
    const type_options = [
      { id: 'text', text: 'Freitext' },
      { id: 'textarea', text: 'Freitext (mehrzeilig)' },
      { id: 'valuelist', text: 'Werteliste (Einfach- oder Mehrfachauswahl aus definierter Werteliste)' },
      { id: 'checkbox', text: 'Checkbox' },
      { id: 'email', text: 'E-Mail Adresse' },
      { id: 'link', text: 'Link' },
    ];

    //
    //-----------------COMPUTED------------------
    //
    const user = computed(() => {
      return userStore.getState().user;
    });
    const userIsAdmin = computed(() => {
      return userStore.isAdmin() && userStore.hasAnyUserRole(['domain-admin']);
    });
    const showValuelistSelect = computed(() => {
      return !createNewValuelist.value && typeValuelist.value;
    });
    const showLoading = computed(() => {
      return loader.value.submitting || loader.value.initialising;
    });

    //
    //-----------------METHODS------------------
    // methods - messages for user
    //

    function clearMessages(params) {
      messages.value = [];
    }
    function pushMessage(params) {
      messages.value.push(params);
    }

    //
    //-----------------METHODS------------------
    // fetching/submitting
    //

    function initValues() {
      input_field_definitions.value = [
        {
          name: 'name',
          label: 'Name des Zusatzfeldes',
          required: true,
          default: attribute.value ? attribute.value.name : null,
        },
        {
          name: 'help',
          label: 'Hilfetext',
          required: true,
          default: attribute.value ? attribute.value.help : null,
        },
        {
          name: 'error',
          label: 'Fehlertext',
          required: true,
          default: attribute.value ? attribute.value.error : null,
        },
      ];
      checkbox_field_definitions.value = [
        {
          name: 'required',
          label: 'Pflichtangabe für Bürgerinnen und Bürger',
          default: attribute.value ? attribute.value.required == 1 : true,
        },
        {
          name: 'required_admin',
          label: 'Pflichtangabe für Verwaltung',
          default: attribute.value ? attribute.value.required_admin == 1 : true,
        },
        {
          name: 'public',
          label: 'Öffentliche Information',
          default: attribute.value ? attribute.value.public == 1 : true,
        },
        {
          name: 'multiselect',
          label: 'Mehrfachauswahl',
          default: attribute.value ? attribute.value.multiselect == 1 : false,
        },
      ];
    }

    function fetchAttribute() {
      clearMessages();
      AttributeRepository.getItem(props.attribute_id)
        .then(data => {
          attribute.value = data;
          initValues();
        })
        .catch(error => {
          console.log(error);
          //TODO show error message
        })
        .finally(() => {
          loader.value.initialising = false;
        });
      // update the object to prepare for POSTing to server
      return 1;
    }

    function fetchValuelistOptions() {
      clearMessages();
      loader.value.fetchingValuelist = true;
      AttributeRepository.getValuelist({ fields: 'id,name' })
        .then(data => {
          console.log(data);
          data.forEach(element => {
            availableValuelistOptions.value.push({
              id: element.id,
              text: element.name,
            });
          });
        })
        .catch(error => {
          console.log(error);
          //TODO show error message
        })
        .finally(() => {
          if (availableValuelistOptions.value.length < 1) {
            createNewValuelist.value = true;
          }
          loader.value.fetchingValuelist = false;
        });
    }

    function putAttribute(values) {
      AttributeRepository.put(props.attribute_id, values)
        .then(data => {
          // success & redirect?
          pushMessage({
            severity: 'success',
            content: 'Zusatzfeld erfolgreich gespeichert.',
          });
          loader.value.submitted = true;
          context.emit('submitted');
          window.location = props.redirect_after_save;
        })
        .catch(error => {
          console.log(error);
          pushMessage({
            severity: 'warning',
            content: 'Zusatzfeld konnte nicht gespeichert werden, bitte versuchen Sie es erneut.',
          });
        })
        .finally(() => {
          loader.value.submitting = false;
        });
    }

    function postAttribute(values) {
      values.code = values.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      if (props.parent_id && props.parent_type) {
        values.parent_id = props.parent_id;
        values.parent_type = props.parent_type;
      }
      AttributeRepository.post(values)
        .then(data => {
          // success & redirect?
          pushMessage({
            severity: 'success',
            content: 'Zusatzfeld erfolgreich gespeichert.',
          });
          loader.value.submitted = true;
          context.emit('submitted');
          window.location = props.redirect_after_save;
        })
        .catch(error => {
          console.log(error);
          pushMessage({
            severity: 'warning',
            content: 'Zusatzfeld konnte nicht gespeichert werden, bitte versuchen Sie es erneut.',
          });
        })
        .finally(() => {
          loader.value.submitting = false;
        });
    }

    function cleanSubmitValues(data) {
      // change select-values from array to single values before submitting
      if (data.visible_to && Array.isArray(data.visible_to)) {
        data.visible_to = data.visible_to[0];
      }
      if (data.type && Array.isArray(data.type)) {
        data.type = data.type[0];
      }
      if (data.valuelistid && Array.isArray(data.valuelistid)) {
        data.valuelistid = data.valuelistid[0];
      }
      return data;
    }

    function onSubmitForm(values) {
      const data = cleanSubmitValues(values);
      console.log(data);
      clearMessages();
      if (props.attribute_id && attribute.value) {
        // PUT to server here
        putAttribute(values);
      } else {
        // POST new to server here

        if (values.type == 'valuelist' && !values.valuelistid) {
          if (newValuelistEntries.value.entries.length < 2) {
            // TODO show error message, saying at least 2 values are required
            pushMessage({
              severity: 'warning',
              content: 'Bitte geben Sie mindestens 2 Werte für die Werteliste an.',
            });
            return;
          }
          if (newValuelistEntries.value.validation.findIndex(element => element == false) >= 0) {
            // TODO show error messageif values are not valid
            pushMessage({
              severity: 'warning',
              content: 'Bitte geben Sie eindeutige Werte ein.',
            });
            return;
          }

          // create entries with code and name values
          const entriesArray = [];
          newValuelistEntries.value.entries.forEach(element => {
            const cleanCode = element.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
            entriesArray.push({
              name: element,
              code: cleanCode,
            });
          });
          // validate entry.code values are unique or add index number to make it unique
          const alreadySeen = {};
          entriesArray.forEach((el, index) => {
            if (alreadySeen[el.code]) el.code = el.code + index;
            else alreadySeen[el.code] = true;
          });

          console.log(entriesArray);
          AttributeRepository.postValuelist({
            name: 'Werteliste zu ' + values.name,
            entries: entriesArray,
          })
            .then(response => {
              console.log(response);
              values.valuelistid = response.id;
              postAttribute(values);
            })
            .catch(error => {
              console.log(error);
              pushMessage({
                severity: 'warning',
                content: 'Werteliste konnte nicht gespeichert werden, bitte versuchen Sie es erneut.',
              });
            });
        } else {
          postAttribute(values);
        }
      }
    }

    //
    //-----------------METHODS------------------
    // helpers
    //

    function onTypeSelect(event, field) {
      if (event.value === 'valuelist') {
        fetchValuelistOptions();
        typeValuelist.value = true;
      } else {
        typeValuelist.value = false;
        createNewValuelist.value = false;
      }
    }
    function showCreateValuelist() {
      createNewValuelist.value = true;
    }
    function updateNewValuelist(value) {
      newValuelistEntries.value = value;
    }

    //
    //-----------------LIFECYCLE / WATCH------------------
    //

    // set props to store for initialisation
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
      if (props.attribute_id) {
        fetchAttribute();
      } else {
        initValues();
        loader.value.initialising = false;
      }
    });

    return {
      // REFS
      loader,
      selectedTopic,
      selectedThought,
      messages,
      showLoading,
      input_field_definitions,
      checkbox_field_definitions,
      visible_to_options,
      type_options,
      availableValuelistOptions,
      createNewValuelist,
      typeValuelist,
      newValuelistEntries,
      attribute,

      // COMPUTED
      user,
      userIsAdmin,
      showValuelistSelect,

      // METHODS
      pushMessage,
      clearMessages,
      onSubmitForm,
      onTypeSelect,
      showCreateValuelist,
      updateNewValuelist,
    };
  },

  components: {
    Loading,
    Message,
    WdwDynbox,
    ValidationForm,
    FormControlInput,
    FormControlCheckbox,
    FormControlSelect,
    ValuelistFormVue,
  },
};
</script>
<style scoped>
.alternative-create-valuelist-btn {
  margin-bottom: 15px;
}
</style>
